import React from 'react';
import { Badge, cx } from '@dynamon-as/ui';
import { AttachFile, Event, WorkHistory } from '@mui/icons-material';
import { type Request, RequestStatus } from '@features/requests/types';
import {
    REQUEST_TYPE_ICONS,
    STATUS_BADGE_VARIANTS,
} from '@features/requests/constants';
import type { RequestTypeKey } from '@features/requests/utils';
import {
    formatDate,
    formatDateRange,
    getRequestTypeKey,
} from '@features/requests/utils';
import { RequestActionsComponent as RequestActions } from '@features/requests/components/RequestActions/RequestActions';
import { useI18n, useRequestTranslations } from '@shared/i18n';

import styles from './RequestListItem.module.css';

/**
 * Props for RequestListItem component
 */
export interface RequestListItemProps {
    /** The request to render */
    request: Request;
    /** Callback when the card is selected */
    onSelect: (id: string) => void;
    /** Callback when user wants to edit */
    onEdit: (id: string) => void;
    /** Callback when user wants to submit */
    onSubmit: (id: string) => void;
}

/** Accent style per request type icon container */
const TYPE_ICON_CLASS: Record<RequestTypeKey, string> = {
    VACATION: styles.typeIconVacation,
    PAID_LEAVE: styles.typeIconPaidLeave,
    SHIFT_PREFERENCE: styles.typeIconShiftPreference,
};

/**
 * Renders a single request as a modern clickable card with
 * type icon, status badge, dates, metadata and actions.
 */
export const RequestListItem = React.memo(function RequestListItem({
    request,
    onSelect,
    onEdit,
    onSubmit,
}: RequestListItemProps): React.ReactElement {
    const { t } = useI18n();
    const { getRequestTypeLabel, getShiftPreferenceLabel, getStatusLabel } =
        useRequestTranslations();

    const typeKey = getRequestTypeKey(request.type);
    const TypeIcon = REQUEST_TYPE_ICONS[typeKey];
    const typeLabel = getRequestTypeLabel(typeKey);
    const statusLabel = getStatusLabel(request.status);

    const dateText =
        request.status === RequestStatus.DRAFT && request.deadline
            ? `${t('requests.deadline')}: ${formatDate(request.deadline)}`
            : (formatDateRange(request.startDate, request.endDate) ??
              t('requests.noDates'));

    const shiftLabel = request.shiftPreference
        ? getShiftPreferenceLabel(request.shiftPreference)
        : null;
    const attachmentCount = request.attachments?.length ?? 0;

    return (
        <li
            className={styles.item}
            data-testid="request-item"
        >
            <button
                type="button"
                className={styles.content}
                onClick={(): void => onSelect(request.id)}
                aria-label={`Se søknad: ${typeLabel}, status: ${request.status}`}
            >
                <span className={cx(styles.typeIcon, TYPE_ICON_CLASS[typeKey])}>
                    <TypeIcon fontSize="small" />
                </span>
                <span className={styles.body}>
                    <span className={styles.titleRow}>
                        <span className={styles.type}>{typeLabel}</span>
                        <Badge
                            text={statusLabel}
                            variant={STATUS_BADGE_VARIANTS[request.status]}
                            size="small"
                            dot
                        />
                    </span>
                    <span className={styles.date}>
                        <Event
                            fontSize="small"
                            className={styles.dateIcon}
                        />
                        {dateText}
                    </span>
                    {(shiftLabel ?? attachmentCount > 0) && (
                        <span className={styles.meta}>
                            {shiftLabel && (
                                <span className={styles.metaItem}>
                                    <WorkHistory
                                        fontSize="small"
                                        className={styles.metaIcon}
                                    />
                                    {t('requests.shiftPreference')}:{' '}
                                    {shiftLabel}
                                </span>
                            )}
                            {attachmentCount > 0 && (
                                <span className={styles.metaItem}>
                                    <AttachFile
                                        fontSize="small"
                                        className={styles.metaIcon}
                                    />
                                    {attachmentCount}{' '}
                                    {t('requests.attachments')}
                                </span>
                            )}
                        </span>
                    )}
                    {request.notes && (
                        <span className={styles.notes}>{request.notes}</span>
                    )}
                </span>
            </button>
            <div className={styles.actions}>
                <RequestActions
                    request={request}
                    onEdit={(): void => onEdit(request.id)}
                    onSubmit={(): void => onSubmit(request.id)}
                    onView={(): void => onSelect(request.id)}
                />
            </div>
        </li>
    );
});
